import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader } from "lucide-react";
import { Plan, formatPrice } from "@/config/subscriptionPlans";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/useToast";
import { useSubscriptionState } from "@/zustand/SubscriptionState";
import {
  createCheckoutSession,
  createPortalSession,
} from "@/server/services/stripeService";
import { SubscriptionPlan } from "@/server/types/subscription";
import TermsModal from "@/components/shared/TermsModal/TermsModal";
import { safeSession } from "@/utils/safeStorage";

interface PlanCheckoutBarProps {
  plan: Plan;
  isYearly: boolean;
  isCompact?: boolean;
}

type PaidPlan = Exclude<SubscriptionPlan, "free">;

export default function PlanCheckoutBar({
  plan,
  isYearly,
  isCompact = false,
}: PlanCheckoutBarProps) {
  const { t } = useTranslation();
  const subscription = useSubscriptionState((s) => s.subscription);
  const [loading, setLoading] = useState(false);
  const [tosOpen, setTosOpen] = useState(false);

  const isFree = plan.id === "free";
  const hasActiveSubscription = subscription.stripe_subscription_id !== null;
  const targetInterval = isYearly ? "year" : ("month" as const);

  // For existing subscribers, use their actual interval; for new users, use the toggle.
  const effectiveIsYearly = hasActiveSubscription
    ? subscription.interval === "year"
    : isYearly;

  const perMonth = effectiveIsYearly
    ? plan.yearlyMonthlyEquiv
    : plan.monthlyPrice;
  const totalToday = effectiveIsYearly ? plan.yearlyPrice : plan.monthlyPrice;
  const annualSavings = plan.annualSavings;

  const renewalDate = subscription.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const handleCheckout = async () => {
    setLoading(true);
    safeSession.set("orion:pre-checkout-plan", subscription.plan);
    try {
      if (hasActiveSubscription) {
        const url = await createPortalSession();
        window.location.href = url;
      } else {
        const url = await createCheckoutSession(
          plan.id as PaidPlan,
          targetInterval,
        );
        window.location.href = url;
      }
      // No setLoading(false) — page navigates away on success
    } catch {
      toast({
        description: t("subscription.checkoutError"),
        variant: "destructive",
        duration: 3000,
      });
      setLoading(false);
    }
  };

  if (isFree) {
    return (
      <div className="px-5 py-4">
        <p className="text-center text-sm text-muted-foreground">
          {subscription.plan === "free"
            ? t("subscription.freePlanCTA")
            : t("subscription.freeGetStarted")}
        </p>
      </div>
    );
  }

  const isCancelling =
    hasActiveSubscription && subscription.cancel_at_period_end;

  const activeStatusText = hasActiveSubscription
    ? renewalDate
      ? isCancelling
        ? t("subscription.expiresOn", { date: renewalDate })
        : t("subscription.activeRenews", { date: renewalDate })
      : t("subscription.active")
    : null;

  // ── Mobile compact layout ────────────────────────────────────
  if (isCompact) {
    return (
      <div className="grid grid-cols-[1fr_auto] items-center gap-4 px-4 py-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[14px] font-semibold leading-tight">
            {plan.name}
          </span>
          <span
            className={cn(
              "text-[11.5px] font-semibold tabular-nums",
              !hasActiveSubscription && effectiveIsYearly
                ? "text-secondary"
                : "text-muted-foreground",
            )}
          >
            {activeStatusText ??
              (effectiveIsYearly
                ? t("subscription.mobileSaveYearly", { amount: annualSavings })
                : t("subscription.monthlyBilling"))}
          </span>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className="text-[14px] font-bold tabular-nums leading-none">
            {formatPrice(perMonth)}
            <span className="text-[11px] font-medium text-muted-foreground">
              {t("subscription.perMonth")}
            </span>
          </span>
          <button
            type="button"
            onClick={handleCheckout}
            disabled={loading}
            className={cn(
              "rounded-lg px-3.5 py-2 text-[12px] font-semibold transition-colors",
              hasActiveSubscription
                ? "border border-border bg-transparent text-foreground hover:bg-muted"
                : "bg-secondary text-secondary-foreground hover:brightness-110",
            )}
          >
            {loading ? (
              <Loader className="size-3.5 animate-spin" />
            ) : hasActiveSubscription ? (
              t("subscription.manageArrow")
            ) : (
              t("subscription.subscribeArrow")
            )}
          </button>
          {!hasActiveSubscription && (
            <button
              type="button"
              onClick={() => setTosOpen(true)}
              className="text-[10px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              {t("subscription.termsLink")}
            </button>
          )}
        </div>
        <TermsModal open={tosOpen} onOpenChange={setTosOpen} />
      </div>
    );
  }

  // ── Desktop 3-column layout ──────────────────────────────────
  return (
    <div
      className={cn(
        "grid items-center gap-6 px-5 py-3.5",
        hasActiveSubscription
          ? "grid-cols-[minmax(0,1fr)_auto]"
          : "grid-cols-[minmax(0,1.2fr)_minmax(0,1.6fr)_auto]",
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-semibold leading-tight">
            {hasActiveSubscription ? (
              <>
                {t("subscription.youreOn")} <b>{plan.name}</b>
              </>
            ) : (
              <>
                {t("subscription.subscribingTo")} <b>{plan.name}</b>
              </>
            )}
          </span>
          <span className="rounded bg-secondary/20 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-widest text-secondary">
            {effectiveIsYearly
              ? t("subscription.yearly")
              : t("subscription.monthly")}
          </span>
        </div>
        <span className="text-[11px] text-muted-foreground">
          {activeStatusText ??
            (effectiveIsYearly
              ? t("subscription.billedYearlyCancel")
              : t("subscription.billedMonthlyCancel"))}
        </span>
      </div>

      {!hasActiveSubscription && (
        <div className="flex gap-5 text-[11px] text-muted-foreground">
          <div className="flex flex-col leading-tight">
            <span>{t("subscription.perMonthLabel")}</span>
            <b className="text-[14px] font-bold tabular-nums text-foreground">
              {formatPrice(perMonth)}
            </b>
          </div>
          <div className="flex flex-col leading-tight">
            <span>
              {effectiveIsYearly ? t("subscription.yearlySavingsLabel") : "—"}
            </span>
            <b
              className={cn(
                "text-[14px] font-bold tabular-nums",
                effectiveIsYearly ? "text-green-400" : "text-foreground",
              )}
            >
              {effectiveIsYearly
                ? `−${formatPrice(annualSavings)}${t("subscription.perYear")}`
                : "—"}
            </b>
          </div>
          <div className="flex flex-col leading-tight">
            <span>{t("subscription.totalTodayLabel")}</span>
            <b className="text-[18px] font-bold tabular-nums leading-none text-foreground">
              {formatPrice(totalToday)}
            </b>
            <span className="text-[10px]">
              {effectiveIsYearly
                ? t("subscription.billedTodayYearly")
                : t("subscription.billedMonthlyShort")}
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-col items-end gap-1">
        <button
          type="button"
          onClick={handleCheckout}
          disabled={loading}
          className={cn(
            "rounded-lg px-5 py-2.5 text-[13px] font-semibold transition-all",
            hasActiveSubscription
              ? "border border-border bg-transparent text-foreground hover:bg-muted"
              : "bg-secondary text-secondary-foreground hover:brightness-110",
          )}
        >
          {loading ? (
            <Loader className="size-4 animate-spin" />
          ) : hasActiveSubscription ? (
            `${t("subscription.managePlan")} →`
          ) : (
            t("subscription.subscribeTo", { name: plan.name })
          )}
        </button>
        {!hasActiveSubscription && (
          <span className="text-[10px] text-muted-foreground">
            {t("subscription.byAgreeing")}{" "}
            <button
              type="button"
              onClick={() => setTosOpen(true)}
              className="underline underline-offset-2 hover:text-foreground"
            >
              {t("subscription.termsLink")}
            </button>
          </span>
        )}
      </div>

      <TermsModal open={tosOpen} onOpenChange={setTosOpen} />
    </div>
  );
}
